import React, { useLayoutEffect, useState } from 'react';

import { sanitizeUrl } from '@braintree/sanitize-url';

import A from '@adsk/alloy-react-a';
import { OpenIcon } from '@adsk/alloy-react-icon';

import { getIconSize } from './getIconSize';

const LinkWithIcon = React.forwardRef<
  HTMLAnchorElement,
  {
    href: string;
    displayUrl: string;
    parentElement: HTMLElement | null;
    showIcon?: boolean;
  }
>(({ href, displayUrl, parentElement, showIcon = true, ...props }, ref) => {
  const [iconSize, setIconSize] = useState(16);

  useLayoutEffect(() => {
    setIconSize(getIconSize(parentElement));
  }, [parentElement]);

  return (
    <span style={{ display: 'inline', maxWidth: '100%' }}>
      <A
        ref={ref}
        href={href}
        target="_blank"
        style={{
          textDecoration: 'underline',
          fontWeight: 'normal',
          display: 'inline',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
        {...props}
      >
        {displayUrl}
        {showIcon && (
          <OpenIcon
            size={iconSize}
            data-testid="open-icon"
            style={{
              marginLeft: '2px',
              marginRight: '4px',
              position: 'relative',
              top: iconSize === 24 ? '5px' : '2px',
            }}
          />
        )}
      </A>
    </span>
  );
});

LinkWithIcon.displayName = 'LinkWithIcon';

export const parseTextWithUrls = (
  text: string,
  parentElement: HTMLElement | null,
  showUrlsIcons: boolean,
) => {
  if (!text) return null;

  const urlRegex = /(https?:\/\/|www\.)\S{1,2048}/g;
  const parts = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  urlRegex.lastIndex = 0;

  while ((match = urlRegex.exec(text)) !== null) {
    const url = match[0];
    const startIndex = match.index;

    if (startIndex > lastIndex) {
      parts.push(text.substring(lastIndex, startIndex));
    }

    const punctuationMatch = url.match(/[.,;:!?)]$/);
    let displayUrl = url;
    let trailingPunctuation = '';

    if (punctuationMatch) {
      trailingPunctuation = punctuationMatch[0];
      displayUrl = url.slice(0, -1);
    }

    let linkHref = displayUrl.startsWith('www.')
      ? `https://${displayUrl}`
      : displayUrl;

    // Always sanitize URLs
    linkHref = sanitizeUrl(linkHref);

    parts.push(
      <LinkWithIcon
        key={`link-${startIndex}`}
        href={linkHref}
        displayUrl={displayUrl}
        parentElement={parentElement}
        showIcon={showUrlsIcons}
      />,
    );

    if (trailingPunctuation) {
      parts.push(trailingPunctuation);
    }

    lastIndex = startIndex + url.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length ? parts : text;
};
